// navigation-button.component.ts

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnInit
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import { PopoutButtonComponent, ToolkitApiService } from '@jeppesen-foreflight/jmkw-toolkit';
import { JepGroupLayer, JepLayer } from '@jeppesen-foreflight/jmkw-types';
import { NavigationLayer } from '../../layer/navigation.layer';
import { NavigationPopoutComponent } from './navigation-button-popout/navigation-popout.component';
import Collection from '@arcgis/core/core/Collection';
import type { ResourceHandle } from "@arcgis/core/core/Handles";

@Component({
  selector: 'jmkw-navigation-button',
  standalone: true,
  imports: [PopoutButtonComponent],
  templateUrl: './navigation-button.component.html',
  styleUrls: ['./navigation-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NavigationButtonComponent implements OnInit {
  private readonly _api = inject(ToolkitApiService);
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _destroyRef = inject(DestroyRef);

  private _layerVisibilityIHandle?: ResourceHandle;

  readonly iconName = 'icon-navigation';
  readonly tooltipText = 'Navigation Options';
  readonly componentRef = NavigationPopoutComponent;
  readonly automationKey = 'navigation-button';
  active = false;

  ngOnInit(): void {
    this._api
      .onLayerReady$<NavigationLayer>('navigation')
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((layer) => {
        this._destroyLayerVisibilityWatcher();
        const layers = layer?.layers;
        if (layers) {
          this.active = this._collectVisibleStates(layers).some(Boolean);
          this._cdr.markForCheck();
          this._setupLayerVisibilityWatcher(layers);
        }
      });
  }

  private _destroyLayerVisibilityWatcher(): void {
    this._layerVisibilityIHandle?.remove();
  }

  private _collectVisibleStates(
    layers: Collection<JepLayer | JepGroupLayer>
  ): boolean[] {
    const states: boolean[] = [];
    layers.forEach((layer) => {
      states.push(layer.visible);
      const nested = (layer as JepGroupLayer).layers;
      if (nested?.length) {
        states.push(...this._collectVisibleStates(nested as Collection<JepLayer | JepGroupLayer>));
      }
    });
    return states;
  }

  private _setupLayerVisibilityWatcher(
    layers: Collection<JepLayer | JepGroupLayer>
  ): void {
    this._layerVisibilityIHandle = reactiveUtils.watch(
      () => this._collectVisibleStates(layers),
      (visibleStates) => {
        this.active = visibleStates.some((isVisible) => isVisible);
        this._cdr.markForCheck();
      }
    );
  }
}
